import { apiClient } from '@kss/api';

/**
 * Billing ↔ Payments integration helper.
 *
 * The billing flows set `paidAmount`/`balance` on the invoice header but
 * never wrote a payment record, so the Payments ledger stayed empty and
 * invoice balances weren't backed by real receipts. These helpers create
 * the matching SalesPayment / PurchasePayment rows.
 *
 * Field shapes verified against the live swagger:
 *   SalesPayment:    salesInvoiceId, paymentDate, method, amount, txnRef, paymentStatus
 *   PurchasePayment: supplierId(req), purchaseInvoiceId, paymentDate, method, amount, ...
 *
 * (PosSalePayment has no endpoint — POS records a SalesPayment against the
 *  SalesInvoice it creates.)
 *
 * Best-effort: a payment-post failure never throws (the invoice is already
 * saved), so a transient error can't block billing.
 */

export async function postSalesPayment(opts: {
  salesInvoiceId: number | string;
  amount: number;
  method?: string;
  paymentDate?: string;
  docNumber?: string;
}): Promise<boolean> {
  if (!opts.salesInvoiceId || !(Number(opts.amount) > 0)) return false;
  const num = opts.docNumber ?? String(opts.salesInvoiceId);
  try {
    await apiClient.post('/sales/SalesPayment', {
      code: `PAY-${num}`,
      name: `Payment ${num}`,
      status: 0,
      salesInvoiceId: Number(opts.salesInvoiceId),
      paymentDate: opts.paymentDate ?? new Date().toISOString(),
      method: opts.method || 'Cash',
      amount: Number(opts.amount),
      currency: 'INR',
      paymentStatus: 'Completed',
    });
    return true;
  } catch {
    return false;
  }
}

export async function postPurchasePayment(opts: {
  purchaseInvoiceId: number | string;
  supplierId: number | string;
  amount: number;
  method?: string;
  paymentDate?: string;
  docNumber?: string;
}): Promise<boolean> {
  if (!opts.supplierId || !(Number(opts.amount) > 0)) return false;
  const num = opts.docNumber ?? String(opts.purchaseInvoiceId);
  try {
    await apiClient.post('/purchase/PurchasePayment', {
      code: `PAY-${num}`,
      name: `Payment ${num}`,
      status: 0,
      paymentNumber: `PAY-${num}`,
      paymentDate: opts.paymentDate ?? new Date().toISOString(),
      supplierId: Number(opts.supplierId),
      purchaseInvoiceId: opts.purchaseInvoiceId != null ? Number(opts.purchaseInvoiceId) : null,
      method: opts.method || 'Cash',
      amount: Number(opts.amount),
      currency: 'INR',
      paymentStatus: 'Completed',
    });
    return true;
  } catch {
    return false;
  }
}

/** Derive the header paymentStatus from amount paid vs grand total. */
export function paymentStatusFor(paid: number, grand: number): 'Paid' | 'Partial' | 'Unpaid' {
  if (paid >= grand && grand > 0) return 'Paid';
  if (paid > 0) return 'Partial';
  return 'Unpaid';
}
